import React, { useEffect, useState} from 'react'
import axios from 'axios'
import { BASE_URL } from '../../utils/constants';
import { FaBell, FaBoxArchive } from "react-icons/fa6";
import { MdExitToApp } from "react-icons/md";
import { Table, Form, Button } from 'react-bootstrap'
import { Oval } from "react-loader-spinner"
import Modal from 'react-bootstrap/Modal'
import { useNavigate, Link } from 'react-router-dom';
import moment from 'moment';
import _ from "underscore"
import { AiFillWarning, AiFillCaretUp, AiFillCaretDown } from "react-icons/ai";
import CircleProgressBar from '../../components/CircleProgressBar/CircleProgressBar';
import Pagination from '../../components/Pagination/Pagination';

import "./PatientCompliance.scss"

const studyMap = {
  "shoulder arthroscopy": "SAs",
  "shoulder arthroplasty": "SAp",
  "elbow": "E"
}

const studyColors = {
  "shoulder arthroscopy": "rgb(38, 197, 102)", // Green
  "shoulder arthroplasty": "rgb(255, 193, 7)", // Yellow/Orange
  "elbow": "rgb(220, 53, 69)" // Red
}

// Dynamic color function based on compliance percentage
const getComplianceColor = (percentage) => {
  if (percentage >= 95) return "rgb(38, 197, 102)"  // Green
  if (percentage >= 90) return "rgb(255, 193, 7)"   // Orange
  return "rgb(220, 53, 69)"                         // Red
}

const PatientCompliance = ({ user, logUserOut }) => {
  const [loading, setLoading] = useState(true)
  const [nonCompliantPatients, setNonCompliantPatients] = useState([])
  const [studyCompliance, setStudyCompliance] = useState({})
  const [sorting, setSorting] = useState({ field: 'alertDate', ascending: true })
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const navigate = useNavigate();
  const config = {
    headers: {
      Authorization: `Bearer ${user.token}`,
    },
  }

  useEffect(() => {
    const fetchComplianceData = async () => {
      try {
        setLoading(true)
        
        // Fetch compliance percentages per study
        const complianceResponse = await axios.get(`${BASE_URL}/api/v1/patient/patient-compliance-percentages-per-study`, config)
        const studyData = complianceResponse.data
        setStudyCompliance(studyData)
        
        // Fetch non-compliant patients for the table
        const nonCompliantResponse = await axios.get(`${BASE_URL}/api/v1/patient/non-compliant-patients/`, config)
        const nonCompliantPatients = nonCompliantResponse.data.nonCompliantPatients
        setNonCompliantPatients(nonCompliantPatients)
        
        setLoading(false)
      }
      catch (error) {
        console.log(error)
        const message = error.response && error.response.data.message ? error.response.data.message : error.message
        if (error.response && (error.response.status === 401 || message === 'Not authorized, token failed')) {
          logUserOut()
        }
        setLoading(false)
      }
    }
    
    fetchComplianceData()
  }, [])

  useEffect(() => {
    setNonCompliantPatients(sorting.ascending? _.sortBy(nonCompliantPatients, sorting.field) : _.sortBy(nonCompliantPatients, sorting.field).reverse())
    setCurrentPage(1) // Reset to page 1 on sorting change
  }, [sorting]);

  const handleProgressBarClick = (study) => {
    // Filter table by study or navigate to study-specific view
    console.log(`Clicked on ${study} compliance`)
  }

  const capitalizeStudyName = (study) => {
    if (study === 'elbow') return 'Elbow'
    if (study === 'shoulder arthroplasty') return 'Shoulder Arthroplasty'
    if (study === 'shoulder arthroscopy') return 'Shoulder Arthroscopy'
    return study
  }

  // Logic to handle UI-side pagination
  const totalItems = nonCompliantPatients.length;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentItems = nonCompliantPatients.slice(startIndex, endIndex);

  return (
    <div className="patient-compliance">
      {loading ? (<Oval
          height={80}
          width={80}
          color="#4caeff"
          wrapperStyle={{}}
          wrapperClass="loader"
          visible={true}
          ariaLabel='oval-loading'
          secondaryColor="#4caeff"
          strokeWidth={2}
          strokeWidthSecondary={2}
        />
        ) : (
        <>
          <div className="patient-compliance-header">
            <h2 className="logo"><AiFillWarning /> Non-compliant Patients</h2>
            {studyCompliance.total && (
              <div className="compliance-summary">
                <span className="total-count">
                  {studyCompliance.total.nonCompliantPatients || 0} of {studyCompliance.total.totalPatients || 0} patients non-compliant
                </span>
                <span className="overall-percentage">
                  ({studyCompliance.total.compliancePercentage || 100}% compliance)
                </span>
              </div>
            )}
          </div>

          {/* Progress Bars Section */}
          <div className="progress-bars-container">
            {Object.keys(studyMap).map(study => {
              const studyKey = study === 'shoulder arthroscopy' ? 'shoulderArthroscopy' :
                               study === 'shoulder arthroplasty' ? 'shoulderArthroplasty' : 
                               'elbow'
              const data = studyCompliance[studyKey] || { compliancePercentage: 100, nonCompliantPatients: 0, totalPatients: 0 }
              return (
                <div key={study} className="progress-item">
                  <CircleProgressBar 
                    percentage={data.compliancePercentage || 100} 
                    colour={getComplianceColor(data.compliancePercentage || 100)} 
                    width="100px" 
                    height="100px" 
                    radius="35" 
                    onClickAction={() => handleProgressBarClick(study)}
                  />
                  <div className="progress-label">
                    <span className="study-name">
                      {capitalizeStudyName(study)}
                    </span>
                    <div className="study-details">
                      {studyMap[study]} - {data.nonCompliantPatients || 0}/{data.totalPatients || 0} non-compliant
                    </div>
                  </div>
                </div>
              )
            })}
          </div>

          <div className='patient-compliance-table'>
            <Table striped bordered={false} hover>
              <thead>
                <tr>
                  <th className='custom-id' onClick={() => setSorting({field: "customPatientId", ascending: !sorting.ascending})}>Patient ID {sorting.field === "customPatientId"? sorting.ascending? <AiFillCaretUp/>: <AiFillCaretDown/> : ""}</th>
                  <th className='study'>Study</th>
                  <th className='event-date'>Event Date</th>
                  <th className='milestone'>Milestone</th>
                  <th className='date-of-submission'>Date of Submission</th>
                  <th className='survey-deadline'>Survey Deadline</th>
                  <th className='actions'>Actions</th>
                  <th className='action-buttons'></th>
                </tr>
              </thead>
              <tbody>
                {currentItems.length > 0 ? currentItems.map((surveyOutlier, index) => {
                  return (
                    <tr key={index}>
                      <td>{surveyOutlier.customPatientId}</td>
                      <td>{studyMap[surveyOutlier.study]} ({surveyOutlier.treatmentSide[0]})</td>
                      <td>{moment(surveyOutlier.eventDate).format("DD/MM/YYYY")}</td>
                      <td>{surveyOutlier.stage}</td>
                      <td>{surveyOutlier.dateOfSubmission? moment(surveyOutlier.dateOfSubmission).format("DD/MM/YYYY") : "n/a"}</td>
                      <td>{moment(surveyOutlier.surveyDeadline).format("DD/MM/YYYY")}</td>
                      <td>
                        <Link to={`/patient/${surveyOutlier.customPatientId}/event/${surveyOutlier.eventId}/${surveyOutlier.study.replace(" ", "-")}/${surveyOutlier.treatmentSide.toLowerCase()}/survey/${surveyOutlier.stage.replace(" ", "-")}/${surveyOutlier.dateOfSubmission? "edit": "new"}`}>View Survey</Link></td>
                      <td className='action-buttons'>
                        <div>
                          <Button variant="primary" onClick={() => navigate(`/patients/${surveyOutlier.customPatientId}`)}><MdExitToApp /></Button>
                        </div>
                      </td>
                    </tr>
                  )
                }) : <tr><td colSpan={100} style={{textAlign: "center"}}>100% compliance rate</td></tr>}
              </tbody>
            </Table>
          </div>
          <Pagination
            currentPage={currentPage}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
          />
        </>)}    
    </div>
  )
}

export default PatientCompliance